import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { MTGCard } from 'types/interfaces/mtgCard';
import { useGetCardPrintingsQuery } from 'store/services/cards/search/cardsSearchApi';
import { UpdateHaveListCardRequest } from 'store/services/collectionList/haveList/haveListApi';
import { FoilCardImage } from 'components/FoilCardImage';
import './EditHaveListCardModal.css';

interface Props {
  isOpen: boolean;
  card: CollectionList | null;
  onClose: () => void;
  onSave: (updates: UpdateHaveListCardRequest) => Promise<void>;
}


const CONDITION_OPTIONS = [
  { value: CardCondition.NEAR_MINT, label: 'Near Mint (NM)' },
  { value: CardCondition.SLIGHTLY_PLAYED, label: 'Lightly Played (LP)' },
  { value: CardCondition.MODERATELY_PLAYED, label: 'Moderately Played (MP)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ja', label: 'Japanese' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
  { value: 'zhs', label: 'Chinese Simplified' },
];

const EditHaveListCardModal: React.FC<Props> = ({ isOpen, card, onClose, onSave }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSetCode, setSelectedSetCode] = useState<string>('');
  const [selectedFoil, setSelectedFoil] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<CardCondition>(CardCondition.NEAR_MINT);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Lazy-load printings when modal opens
  // Handle both scryfallId (backend) and scryfallID (TypeScript interface) casings
  const scryfallId = (card?.card as any)?.scryfallId || card?.card?.scryfallID || '';
  const { data: printings, isLoading: isPrintingsLoading } = useGetCardPrintingsQuery(
    scryfallId,
    { skip: !isOpen || !scryfallId }
  );

  // Initialize form with current card values
  useEffect(() => {
    if (card && isOpen) {
      setQuantity(card.quantity || 1);
      setSelectedSetCode(card.card?.setCode || '');
      setSelectedFoil(card.foil || false); // Use UserMTGCardHave.foil, not card printing's foil
      setSelectedCondition(card.condition || CardCondition.NEAR_MINT);
      setSelectedLanguage(card.card?.lang || 'en');
    }
  }, [card, isOpen]);

  // Group printings by set
  const setOptions = useMemo(() => {
    if (!printings) return [];

    const uniqueSets = new Map<string, { code: string; name: string; releaseDate: string }>();

    (printings as any[]).forEach((p: any) => {
      if (p.setCode && !uniqueSets.has(p.setCode)) {
        uniqueSets.set(p.setCode, {
          code: p.setCode,
          name: p.setName || p.setCode,
          releaseDate: p.releasedAt || ''
        });
      }
    });

    return Array.from(uniqueSets.values())
      .sort((a, b) => {
        // Sort by release date (newest first)
        if (a.releaseDate && b.releaseDate) {
          return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
        }
        return 0;
      });
  }, [printings]);

  // Filter sets by search term
  const filteredSets = useMemo(() => {
    if (!searchTerm) return setOptions;

    const lower = searchTerm.toLowerCase();
    return setOptions.filter(set =>
      set.name.toLowerCase().includes(lower) ||
      set.code.toLowerCase().includes(lower)
    );
  }, [setOptions, searchTerm]);

  // Get available languages for selected set (regardless of foil)
  const availableLanguages = useMemo(() => {
    if (!printings || !selectedSetCode) return [];

    const langs = new Set<string>();
    (printings as any[]).forEach((p: any) => {
      if (p.setCode === selectedSetCode) {
        langs.add(p.lang || 'en');
      }
    });

    return LANGUAGE_OPTIONS.filter(opt => langs.has(opt.value));
  }, [printings, selectedSetCode]);

  // Check if foil is available for selected set + language
  // Backend stores one card per set/language with both prices, so check price data
  const foilAvailability = useMemo(() => {
    if (!printings || !selectedSetCode || !selectedLanguage) {
      return { foil: false, nonFoil: false };
    }

    const matches = (printings as any[]).filter((p: any) =>
      p.setCode === selectedSetCode && p.lang === selectedLanguage
    );

    // Check price data to determine availability since backend doesn't have separate records
    return {
      foil: matches.some((p: any) => {
        const priceData = Array.isArray(p.price) ? p.price[0] : p.price;
        return priceData?.usdFoil !== null && priceData?.usdFoil !== undefined;
      }),
      nonFoil: matches.some((p: any) => {
        const priceData = Array.isArray(p.price) ? p.price[0] : p.price;
        return priceData?.usd !== null && priceData?.usd !== undefined;
      })
    };
  }, [printings, selectedSetCode, selectedLanguage]);

  // Find matching cardId
  // Note: Backend stores one card per set/language with both foil and non-foil prices
  // So we match by set and language only, not foil status
  const targetCardId = useMemo(() => {
    if (!printings || !selectedSetCode) return null;

    const match = (printings as any[]).find((p: any) =>
      p.setCode === selectedSetCode &&
      p.lang === selectedLanguage
    );

    return match?.id || null;
  }, [printings, selectedSetCode, selectedLanguage]);

  // Get scryfallID for selected combination
  const displayScryfallId = useMemo(() => {
    if (!printings || !targetCardId) return card?.card?.scryfallID;

    const match = printings.find((p: MTGCard) => p.id === targetCardId);
    return match?.scryfallID || card?.card?.scryfallID;
  }, [printings, targetCardId, card?.card?.scryfallID]);

  // Get price for selected combination
  const estimatedPrice = useMemo(() => {
    if (!printings || !targetCardId) return null;

    const match = printings.find((p: MTGCard) => p.id === targetCardId);
    if (!match || !match.price || match.price.length === 0) return null;

    const priceData = match.price[0];
    if (!priceData) return null;

    return selectedFoil ? priceData.usdFoil : priceData.usd;
  }, [printings, targetCardId, selectedFoil]);

  const handleSave = async () => {
    if (!card || !targetCardId) {
      return;
    }

    setIsSaving(true);
    try {
      const updates: UpdateHaveListCardRequest = {
        haveListRecordId: card.id, // UserMTGCardHave record ID
        quantity,
        condition: selectedCondition,
        foil: selectedFoil,
      };

      // Only include newCardId if we're changing the printing (set/language)
      if (targetCardId !== card.card?.id) {
        updates.newCardId = targetCardId;
      }

      await onSave(updates);
      onClose();
    } catch (error) {
      console.error('Failed to update card:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen || !card) return null;

  const modalContent = (
    <div className="edit-modal-overlay" onClick={handleClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Edit: {card.cardName || card.card?.name}</h2>
          <button className="edit-close-button" onClick={handleClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="edit-modal-content">
          {/* Card Image */}
          <div className="edit-card-image-container">
            {displayScryfallId && (
              <FoilCardImage
                src={AssetUrls.cards.getCardImage(displayScryfallId, 'normal')}
                alt={card.cardName || card.card?.name || 'Card'}
                className="edit-card-image"
                foil={selectedFoil}
                borderRadius="12px"
                onError={(e) => {
                  const img = e.currentTarget;
                  const currentSrc = img.src;

                  // First try backend-provided fallback URLs if available
                  const fallbacks = (card.card as any)?.normal_fallbacks;
                  if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                    img.dataset.fallbackAttempted = 'true';
                    img.src = fallbacks[0];
                    return;
                  }

                  // Otherwise, try different image formats using scryfallId
                  if (displayScryfallId && !img.dataset.formatFallbackComplete) {
                    // Determine current format from URL
                    if (currentSrc.includes('/normal/')) {
                      // Normal format failed, try small
                      img.src = AssetUrls.cards.getCardImage(displayScryfallId, 'small');
                      return;
                    } else if (currentSrc.includes('/small/')) {
                      // Small format failed, try large
                      img.src = AssetUrls.cards.getCardImage(displayScryfallId, 'large');
                      return;
                    } else if (currentSrc.includes('/large/')) {
                      // Large format failed, mark as complete and use placeholder
                      img.dataset.formatFallbackComplete = 'true';
                    }
                  }

                  // If all fallbacks failed, use default card back
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
          </div>

          {/* Quantity */}
          <div className="edit-form-group">
            <label className="edit-label">Quantity</label>
            <div className="edit-quantity-controls">
              <button
                className="edit-quantity-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <FontAwesomeIcon icon="minus" />
              </button>
              <input
                type="number"
                className="edit-quantity-input"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
              />
              <button
                className="edit-quantity-btn"
                onClick={() => setQuantity(quantity + 1)}
              >
                <FontAwesomeIcon icon="plus" />
              </button>
            </div>
          </div>

          {/* Set Selection */}
          <div className="edit-form-group">
            <label className="edit-label">Set / Printing</label>
            {isPrintingsLoading ? (
              <div className="edit-loading">Loading printings...</div>
            ) : (
              <>
                <input
                  type="text"
                  className="edit-input"
                  placeholder="Search sets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="edit-select"
                  value={selectedSetCode}
                  onChange={(e) => setSelectedSetCode(e.target.value)}
                >
                  <option value="">Select a set...</option>
                  {filteredSets.map(set => (
                    <option key={set.code} value={set.code}>
                      {set.name} ({set.code.toUpperCase()})
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Foil */}
          <div className="edit-form-group">
            <label className="edit-checkbox-label">
              <input
                type="checkbox"
                checked={selectedFoil}
                onChange={(e) => setSelectedFoil(e.target.checked)}
                disabled={!foilAvailability.foil}
              />
              <span>Foil version</span>
              {!foilAvailability.foil && <span className="edit-unavailable">(Not available)</span>}
            </label>
          </div>

          {/* Condition */}
          <div className="edit-form-group">
            <label className="edit-label">Condition</label>
            <select
              className="edit-select"
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value as CardCondition)}
            >
              {CONDITION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Language */}
          <div className="edit-form-group">
            <label className="edit-label">Language</label>
            <select
              className="edit-select"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {availableLanguages.length > 0 ? (
                availableLanguages.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))
              ) : (
                <option value={selectedLanguage}>No languages available</option>
              )}
            </select>
          </div>

          {/* Price Preview */}
          <div className="edit-price-section">
            <div className="edit-price-label">Estimated value:</div>
            <div className="edit-price-value">
              {estimatedPrice !== null && estimatedPrice !== undefined
                ? `$${estimatedPrice.toFixed(2)}`
                : 'Price unavailable'}
            </div>
          </div>

          {/* Validation Warning */}
          {!targetCardId && selectedSetCode && (
            <div className="edit-warning">
              ⚠️ This combination is not available for this card
            </div>
          )}
        </div>

        <div className="edit-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={handleClose}
            isDisabled={isSaving}
            fullWidth={false}
          />
          <MagicButton
            label={isSaving ? "Saving..." : "Save Changes"}
            color="purple"
            onPress={handleSave}
            isDisabled={!targetCardId || isSaving}
            isLoading={isSaving}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditHaveListCardModal;
